import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/AxiosInstance";

// GET /api/financial-years
export const fetchFinancialYearsThunk = createAsyncThunk(
  "financialYears/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/financial-years");
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      return res.data?.data || [];
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// POST /api/financial-years
export const createFinancialYearThunk = createAsyncThunk(
  "financialYears/create",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post("/api/financial-years", payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchFinancialYearsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// PUT /api/financial-years/:id
export const updateFinancialYearThunk = createAsyncThunk(
  "financialYears/update",
  async ({ id, ...payload }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/api/financial-years/${id}`, payload);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchFinancialYearsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// PATCH /api/financial-years/:id/set-current
export const setCurrentFinancialYearThunk = createAsyncThunk(
  "financialYears/setCurrent",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.patch(`/api/financial-years/${id}/set-current`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchFinancialYearsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

// DELETE /api/financial-years/:id
export const deleteFinancialYearThunk = createAsyncThunk(
  "financialYears/delete",
  async ({ id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.delete(`/api/financial-years/${id}`);
      if (!res.data?.success) return rejectWithValue(res.data?.message || "Failed");
      dispatch(fetchFinancialYearsThunk());
      return true;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message || "Server error");
    }
  }
);

const financialYearSlice = createSlice({
  name: "financialYears",
  initialState: {
    list: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearFinancialYearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetch
      .addCase(fetchFinancialYearsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFinancialYearsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchFinancialYearsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load financial years";
      })

      // saving (create/update/delete/set-current)
      .addMatcher(
        (a) =>
          a.type === createFinancialYearThunk.pending.type ||
          a.type === updateFinancialYearThunk.pending.type ||
          a.type === deleteFinancialYearThunk.pending.type ||
          a.type === setCurrentFinancialYearThunk.pending.type,
        (state) => {
          state.saving = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createFinancialYearThunk.fulfilled.type ||
          a.type === updateFinancialYearThunk.fulfilled.type ||
          a.type === deleteFinancialYearThunk.fulfilled.type ||
          a.type === setCurrentFinancialYearThunk.fulfilled.type,
        (state) => {
          state.saving = false;
        }
      )
      .addMatcher(
        (a) =>
          a.type === createFinancialYearThunk.rejected.type ||
          a.type === updateFinancialYearThunk.rejected.type ||
          a.type === deleteFinancialYearThunk.rejected.type ||
          a.type === setCurrentFinancialYearThunk.rejected.type,
        (state, action) => {
          state.saving = false;
          state.error = action.payload || "Operation failed";
        }
      );
  },
});

export const { clearFinancialYearError } = financialYearSlice.actions;
export default financialYearSlice.reducer;
